import { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Group,
  Text,
  Stack,
  TextInput,
  Switch,
  SegmentedControl,
  Divider,
  useMantineTheme,
  ColorInput,
  Collapse,
  useComputedColorScheme,
} from '@mantine/core';
import { IconSettings, IconPalette, IconDroplet } from '@tabler/icons-react';
import { Modal, Button } from '../../../ui';
import {
  useDashboardStore,
  selectSettingsModalContainerChild,
} from '../../../../stores/dashboardStore';
import { getWidget } from '../registry';
import { WidgetSettings } from '../types';
import { pickTextColorForBackground } from '../../../../utils/color';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

// ============================================================================
// ContainerChildSettingsModal Component
// ============================================================================

export interface ContainerChildSettingsModalProps {
  /** Whether the modal is open */
  opened: boolean;
  /** Callback to close the modal */
  onClose: () => void;
}

export function ContainerChildSettingsModal({
  opened,
  onClose,
}: ContainerChildSettingsModalProps) {
  const theme = useMantineTheme();
  const colorScheme = useComputedColorScheme('light');
  const settingsModalContainerChild = useDashboardStore(selectSettingsModalContainerChild);
  const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
  const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

  const accessibilityStyles = {
    lineHeight: LINE_HEIGHT_MAP[lineSpacing],
    letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
  };
  const containers = useDashboardStore((state) => state.containers);
  const updateContainerChildSettings = useDashboardStore(
    (state) => state.updateContainerChildSettings
  );
  const closeContainerChildSettingsModal = useDashboardStore(
    (state) => state.closeContainerChildSettingsModal
  );

  // Get the container and child widget data
  const { container, childWidget } = useMemo(() => {
    if (!settingsModalContainerChild) return { container: null, childWidget: null };
    const container = containers.find(
      (c) => c.instanceId === settingsModalContainerChild.containerInstanceId
    );
    const childWidget = container?.children.find(
      (ch) => ch.instanceId === settingsModalContainerChild.childInstanceId
    );
    return { container, childWidget };
  }, [settingsModalContainerChild, containers]);

  const widgetDefinition = useMemo(() => {
    if (!childWidget) return null;
    return getWidget(childWidget.widgetId);
  }, [childWidget]);

  // Local state for form
  const [localSettings, setLocalSettings] = useState<WidgetSettings>({});

  // Initialize local state when modal opens
  useEffect(() => {
    if (childWidget) {
      setLocalSettings(childWidget.settings || {});
    }
  }, [childWidget]);

  // Handle save
  const handleSave = () => {
    if (settingsModalContainerChild) {
      updateContainerChildSettings(
        settingsModalContainerChild.containerInstanceId,
        settingsModalContainerChild.childInstanceId,
        localSettings
      );
    }
    handleClose();
  };

  // Handle close
  const handleClose = () => {
    closeContainerChildSettingsModal();
    onClose();
  };

  // Update local setting
  const updateSetting = <K extends keyof WidgetSettings>(key: K, value: WidgetSettings[K]) => {
    setLocalSettings((prev) => ({ ...prev, [key]: value }));
  };

  if (!childWidget || !widgetDefinition) {
    return null;
  }

  const Icon = widgetDefinition.icon;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group gap="xs">
          <IconSettings size={20} />
          <Text fw={600} style={accessibilityStyles}>Widget Settings</Text>
        </Group>
      }
      size="md"
    >
      <Stack gap="md">
        {/* Widget Info */}
        <Box
          p="sm"
          style={{
            backgroundColor:
              colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
            borderRadius: theme.radius.md,
          }}
        >
          <Group gap="sm">
            <Box
              style={{
                width: 40,
                height: 40,
                borderRadius: theme.radius.md,
                backgroundColor:
                  colorScheme === 'dark' ? theme.colors.blue[9] : theme.colors.blue[1],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon size={20} />
            </Box>
            <Box>
              <Text fw={600} size="sm" style={accessibilityStyles}>
                {widgetDefinition.name}
              </Text>
              <Text size="xs" c="dimmed" style={accessibilityStyles}>
                {widgetDefinition.description}
              </Text>
            </Box>
          </Group>
        </Box>

        <Divider />

        {/* Custom Title */}
        <TextInput
          label="Custom Title"
          description="Override the default widget title"
          placeholder={widgetDefinition.name}
          value={localSettings.customTitle || ''}
          onChange={(e) => updateSetting('customTitle', e.currentTarget.value || undefined)}
        />

        {/* Theme Variant */}
        <Box>
          <Group gap="xs" mb="xs">
            <IconPalette size={16} />
            <Text size="sm" fw={500} style={accessibilityStyles}>
              Theme
            </Text>
          </Group>
          <SegmentedControl
            value={localSettings.themeVariant || 'default'}
            onChange={(value) =>
              updateSetting('themeVariant', value as WidgetSettings['themeVariant'])
            }
            data={[
              { value: 'default', label: 'Default' },
              { value: 'gradient', label: 'Gradient' },
              { value: 'minimal', label: 'Minimal' },
              { value: 'custom', label: 'Custom' },
            ]}
            fullWidth
          />
        </Box>

        {/* Custom Theme Options - Only show when 'custom' is selected */}
        <Collapse in={localSettings.themeVariant === 'custom'}>
          <Stack gap="sm" pt="xs">
            <Box>
              <Group gap="xs" mb="xs">
                <IconDroplet size={16} />
                <Text size="sm" fw={500} style={accessibilityStyles}>
                  Custom Colors
                </Text>
              </Group>
              <ColorInput
                label="Background Color"
                description="Set a custom background color for this widget"
                placeholder="#ffffff"
                value={localSettings.backgroundColor || ''}
                onChange={(color) => updateSetting('backgroundColor', color || undefined)}
                format="hex"
                swatches={[
                  '#25262b',
                  '#868e96',
                  '#fa5252',
                  '#e64980',
                  '#be4bdb',
                  '#7950f2',
                  '#4c6ef5',
                  '#228be6',
                  '#15aabf',
                  '#12b886',
                  '#40c057',
                  '#82c91e',
                  '#fab005',
                  '#fd7e14',
                ]}
              />
            </Box>

            <ColorInput
              label="Custom Gradient (Optional)"
              description="Use a second color to create a gradient background"
              placeholder="Leave empty for solid color"
              value={localSettings.backgroundGradient || ''}
              onChange={(color) => updateSetting('backgroundGradient', color || undefined)}
              format="hex"
              swatches={[
                '#25262b',
                '#868e96',
                '#fa5252',
                '#e64980',
                '#be4bdb',
                '#7950f2',
                '#4c6ef5',
                '#228be6',
                '#15aabf',
                '#12b886',
                '#40c057',
                '#82c91e',
                '#fab005',
                '#fd7e14',
              ]}
            />

            <Switch
              label="Auto text color"
              description="Automatically pick the best text color for readability based on background"
              checked={localSettings.textColor === 'auto'}
              onChange={(e) =>
                updateSetting('textColor', e.currentTarget.checked ? 'auto' : undefined)
              }
            />

            {localSettings.backgroundColor && (
              <Box
                p="md"
                style={{
                  borderRadius: theme.radius.md,
                  background: localSettings.backgroundGradient
                    ? `linear-gradient(135deg, ${localSettings.backgroundColor}, ${localSettings.backgroundGradient})`
                    : localSettings.backgroundColor,
                  color:
                    localSettings.textColor === 'auto'
                      ? pickTextColorForBackground(localSettings.backgroundColor)
                      : undefined,
                }}
              >
                <Text size="sm" fw={500} style={accessibilityStyles}>
                  Preview
                </Text>
                <Text size="xs" style={accessibilityStyles}>This is how your widget will look</Text>
              </Box>
            )}
          </Stack>
        </Collapse>

        {/* Show Header */}
        <Switch
          label="Show header"
          description="Hide header for a cleaner look"
          checked={localSettings.showHeader !== false}
          onChange={(e) => updateSetting('showHeader', e.currentTarget.checked)}
        />

        <Divider />

        {/* Actions */}
        <Group justify="flex-end" gap="sm">
          <Button variant="subtle" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Save Settings</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export default ContainerChildSettingsModal;
